import { Order } from '../order/types'
import { State } from './reducer'
import {
  ListTradingOrder,
  ListTradingSecurity,
  WorkingListTradingOrder
} from './types'
import { IdentifierResolutions } from './helpers'

export const listTradingSecuritiesFetch = (listId: number) =>
  ({
    type: 'listTrading.fetch',
    payload: listId
  } as const)
export type ListTradingSecuritiesFetchAction = ReturnType<
  typeof listTradingSecuritiesFetch
>

export const listTradingSecuritiesFetchFail = (error: unknown) =>
  ({
    type: 'listTrading.fetchFail',
    payload: error,
    error: true
  } as const)
export type ListTradingSecuritiesFetchFailAction = ReturnType<
  typeof listTradingSecuritiesFetchFail
>

export const listTradingSecuritiesFetchSuccess = (
  securitiesList: ListTradingSecurity[]
) =>
  ({
    type: 'listTrading.fetchSuccess',
    payload: securitiesList
  } as const)
export type ListTradingSecuritiesFetchSuccessAction = ReturnType<
  typeof listTradingSecuritiesFetchSuccess
>

export const createListTradingList = (
  name: string,
  orders: ListTradingOrder[]
) =>
  ({
    type: 'listTrading.createList',
    payload: { name, orders }
  } as const)
export type CreateListTradingListAction = ReturnType<
  typeof createListTradingList
>

export const createListTradingListFailure = (message: string) =>
  ({
    type: 'listTrading.createListFailure',
    payload: { message }
  } as const)
export type CreateListTradingListFailureAction = ReturnType<
  typeof createListTradingListFailure
>

export const clearCreateListTradingListError = () =>
  ({
    type: `listTrading.clearCreateListTradingListError`
  } as const)
export type ClearCreateListTradingListErrorAction = ReturnType<
  typeof clearCreateListTradingListError
>

export const updateListTradingList = (
  listId: number,
  orders: WorkingListTradingOrder[]
) =>
  ({
    type: 'listTrading.updateList',
    payload: { listId, orders }
  } as const)
export type UpdateListTradingListAction = ReturnType<
  typeof updateListTradingList
>

export const updateListTradingListSuccess = (
  orders: WorkingListTradingOrder[]
) =>
  ({
    type: 'listTrading.updateListSuccess',
    payload: orders
  } as const)
export type UpdateListTradingListSuccessAction = ReturnType<
  typeof updateListTradingListSuccess
>

export const createWorkingListTradingOrder = (
  securityId: number,
  watchlistId: number,
  order: WorkingListTradingOrder
) =>
  ({
    type: `listTrading.createWorkingListTradingOrder`,
    payload: { ...order, securityId, watchlistId }
  } as const)
export type CreateWorkingListTradingOrderAction = ReturnType<
  typeof createWorkingListTradingOrder
>

export const setListTradingWatchlistId = (watchlistId: State['watchlistId']) =>
  ({
    type: `listTrading.setListTradingWatchlistId`,
    payload: watchlistId
  } as const)
export type SetListTradingWatchlistIdAction = ReturnType<
  typeof setListTradingWatchlistId
>

export const deleteSecurityFromList = (listId: number, securityId: number) =>
  ({
    type: 'listTrading.deleteSecurity',
    payload: { listId, securityId }
  } as const)
export type DeleteListTradingSecurityAction = ReturnType<
  typeof deleteSecurityFromList
>

export const deleteListTradingSecurities = (
  listId: number,
  securityIds: number[]
) =>
  ({
    type: 'listTrading.deleteSecurities',
    payload: { listId, securityIds }
  } as const)
export type DeleteListTradingSecuritiesAction = ReturnType<
  typeof deleteListTradingSecurities
>

export const setListTradingTransactionId = (id: number) =>
  ({
    type: 'listTrading.setListTradingTransactionId',
    payload: id
  } as const)
export type SetListTradingTransactionIdAction = ReturnType<
  typeof setListTradingTransactionId
>

export const saveNewListTradingRows = (
  watchlistId: number,
  rows: ListTradingOrder[]
) =>
  ({
    type: `listTrading.saveNewListTradingRows`,
    payload: { watchlistId, rows }
  } as const)
export type SaveNewListTradingRowsAction = ReturnType<
  typeof saveNewListTradingRows
>

export const addListTradingRows = (
  watchlistId: number,
  rows: ListTradingOrder[]
) =>
  ({
    type: `listTrading.addListTradingRows`,
    payload: { watchlistId, rows }
  } as const)
export type AddListTradingRowsAction = ReturnType<typeof addListTradingRows>

export const resetListTradingTransactionId = () =>
  ({
    type: 'listTrading.resetListTradingTransactionId'
  } as const)
export type ResetListTradingTransactionIdAction = ReturnType<
  typeof resetListTradingTransactionId
>

export const selectListTradingSecurities = (
  securityIds: Array<ListTradingSecurity['id']>
) =>
  ({
    type: 'listTrading.selectListTradingSecurities',
    payload: securityIds
  } as const)
export type SelectListTradingSecuritiesAction = ReturnType<
  typeof selectListTradingSecurities
>

export const toggleSuborderSelection = (
  securityId: ListTradingSecurity['id'],
  orderId: Order['id']
) =>
  ({
    type: `listTrading.toggleSuborderSelection`,
    payload: { securityId, orderId }
  } as const)
export type ToggleSuborderSelectionAction = ReturnType<
  typeof toggleSuborderSelection
>

export const toggleCancelSubOrderSelection = (
  securityId: ListTradingSecurity['id'],
  orderId: Order['id']
) =>
  ({
    type: `listTrading.toggleCancelSubOrderSelection`,
    payload: { securityId, orderId }
  } as const)
export type ToggleCancelSubOrderSelectionAction = ReturnType<
  typeof toggleCancelSubOrderSelection
>

export const selectSecurityOrdersToCancel = (
  securityId: ListTradingSecurity['id'],
  orderIds: Array<Order['id']>
) =>
  ({
    type: `listTrading.selectSecurityOrdersToCancel`,
    payload: { securityId, orderIds }
  } as const)
export type SelectSecurityOrdersToCancelAction = ReturnType<
  typeof selectSecurityOrdersToCancel
>

export const deselectSecurityOrdersToCancel = (
  securityId: ListTradingSecurity['id']
) =>
  ({
    type: `listTrading.deselectSecurityOrdersToCancel`,
    payload: { securityId }
  } as const)
export type DeselectSecurityOrdersToCancelAction = ReturnType<
  typeof deselectSecurityOrdersToCancel
>

export const cancelListTradingOrders = (watchlistId: number) =>
  ({
    type: `listTrading.cancelListTradingOrders`,
    payload: { watchlistId }
  } as const)
export type CancelListTradingOrdersAction = ReturnType<
  typeof cancelListTradingOrders
>

export const tradeSelectedSecurities = (listId: number) =>
  ({
    type: `listTrading.tradeSelectedSecurities`,
    payload: listId
  } as const)
export type TradeSelectedSecuritiesAction = ReturnType<
  typeof tradeSelectedSecurities
>

export const setListTradeErrors = (securities: ListTradingSecurity[]) =>
  ({
    type: `listTrading.setListTradeErrors`,
    payload: securities
  } as const)
export type SetListTradeErrorsAction = ReturnType<typeof setListTradeErrors>

export const validateListTradingIdentifiers = (identifiers: string[]) =>
  ({
    type: 'listTrading.validateIdentifiers',
    payload: identifiers
  } as const)
export type ValidateListTradingIdentifiersAction = ReturnType<
  typeof validateListTradingIdentifiers
>

export const validateIdentifiersComplete = (
  resolutions: IdentifierResolutions,
  valid: boolean
) =>
  ({
    type: 'listTrading.validateIdentifiersComplete',
    payload: { resolutions, valid }
  } as const)
export type ValidateIdentifiersCompleteAction = ReturnType<
  typeof validateIdentifiersComplete
>

export const validateIdentifiersFailed = () =>
  ({
    type: 'listTrading.validateIdentifiersFailed'
  } as const)
export type ValidateIdentifiersFailedAction = ReturnType<
  typeof validateIdentifiersFailed
>

/*
   Watch a single accumulated order via the GetOrderById stream so the FE learns
   when a maker cancels it (remove) or changes its size/price (resolve without
   depth). One stream per order id; unwatchOrder tears a single one down.
*/
export const watchOrder = (
  securityId: ListTradingSecurity['id'],
  orderId: Order['id']
) =>
  ({
    type: 'listTrading.watchOrder',
    payload: { securityId, orderId }
  } as const)
export type WatchOrderAction = ReturnType<typeof watchOrder>

export const unwatchOrder = (orderId: Order['id']) =>
  ({
    type: 'listTrading.unwatchOrder',
    payload: { orderId }
  } as const)
export type UnwatchOrderAction = ReturnType<typeof unwatchOrder>

export const setWatchedOrder = (order: Order) =>
  ({
    type: 'listTrading.setWatchedOrder',
    payload: order
  } as const)
export type SetWatchedOrderAction = ReturnType<typeof setWatchedOrder>

export const removeCancelledOrder = (
  securityId: ListTradingSecurity['id'],
  orderId: Order['id']
) =>
  ({
    type: 'listTrading.removeCancelledOrder',
    payload: { securityId, orderId }
  } as const)
export type RemoveCancelledOrderAction = ReturnType<typeof removeCancelledOrder>

export type ListTradingAction =
  | ListTradingSecuritiesFetchAction
  | ListTradingSecuritiesFetchSuccessAction
  | ListTradingSecuritiesFetchFailAction
  | CreateListTradingListAction
  | CreateListTradingListFailureAction
  | ClearCreateListTradingListErrorAction
  | UpdateListTradingListAction
  | UpdateListTradingListSuccessAction
  | SaveNewListTradingRowsAction
  | CreateWorkingListTradingOrderAction
  | SetListTradingWatchlistIdAction
  | DeleteListTradingSecurityAction
  | DeleteListTradingSecuritiesAction
  | SetListTradingTransactionIdAction
  | ResetListTradingTransactionIdAction
  | SelectListTradingSecuritiesAction
  | CancelListTradingOrdersAction
  | DeselectSecurityOrdersToCancelAction
  | SelectSecurityOrdersToCancelAction
  | ToggleSuborderSelectionAction
  | ToggleCancelSubOrderSelectionAction
  | TradeSelectedSecuritiesAction
  | SetListTradeErrorsAction
  | ValidateListTradingIdentifiersAction
  | ValidateIdentifiersCompleteAction
  | ValidateIdentifiersFailedAction
  | WatchOrderAction
  | UnwatchOrderAction
  | SetWatchedOrderAction
  | RemoveCancelledOrderAction
